import { IContent } from "@vmosolutions/oc-interfaces";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import PostalMime, { type Address, type Attachment, type Email } from "postal-mime";
import { Alert, Box, Chip, Divider, styled, Typography } from "@mui/material";
import { useFileContents } from "src/pages/contentViewers/useFileContents";
import { ViewerShell } from "src/pages/contentViewers/ViewerShell";

interface IEmailViewerProps {
  windowProps: {
    file: IContent;
  };
}

// Content-Security-Policy injected into the rendered email so the body can't phone home: blocks all network
// access by default (no remote images like tracking pixels, no scripts, no frames, no external stylesheets/fonts).
// Inline styles and data: URIs are allowed so the email can render its own formatting and embedded images.
const EMAIL_BODY_CSP = "default-src 'none'; img-src data:; media-src data:; style-src 'unsafe-inline'; font-src data:;";

const EmailBox = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  "& .emailHeaders": {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.default,
    maxHeight: "30%",
    overflowY: "auto",
    flexShrink: 0,
  },
  "& .emailAttachments": {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    alignItems: "center",
    padding: theme.spacing(1, 2),
    flexShrink: 0,
  },
  "& .emailBody": {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  },
  "& .emailBody iframe": {
    width: "100%",
    height: "100%",
    border: "none",
    backgroundColor: theme.palette.common.white,
  },
  "& .emailBodyText": {
    margin: 0,
    padding: theme.spacing(2),
    fontFamily: "monospace",
    fontSize: "0.85em",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));

/** Wraps the email HTML with a restrictive CSP and a `<base target="_blank">` so links open in a new tab. */
const buildEmailSrcDoc = (html: string): string =>
  `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${EMAIL_BODY_CSP}"><base target="_blank"></head><body>${html}</body></html>`;

/** Formats the email date in UTC (explicit + labelled) so it doesn't silently render in the viewer's local zone. */
export const formatDate = (date?: string): string | undefined => {
  if (!date) return undefined;
  const parsed = moment.utc(date);
  return parsed.isValid() ? `${parsed.format("MMM DD, YYYY HH:mm")} UTC` : date;
};

/** Formats a single mailbox as `Name <address>` (or just the address when unnamed). */
export const formatMailbox = (name: string, address: string): string => (name ? `${name} <${address}>` : address);

/** Formats a postal-mime address list (e.g. To/Cc) into a display string, expanding any groups. */
export const formatAddresses = (addresses?: Address[]): string => {
  if (!addresses?.length) return "";
  const parts: string[] = [];
  for (const a of addresses) {
    if (a.address) {
      parts.push(formatMailbox(a.name, a.address));
    } else if (a.group) {
      a.group.forEach((m) => parts.push(formatMailbox(m.name, m.address)));
    }
  }
  return parts.join(", ");
};

const HeaderRow = ({ label, value }: { label: string; value?: string }): JSX.Element | null => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 1, fontSize: "0.85em" }}>
      <Typography component="span" variant="caption" sx={{ fontWeight: 700, minWidth: "60px" }}>
        {label}
      </Typography>
      <Typography component="span" variant="caption" sx={{ wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
};

export function EmailViewerWindow(props: IEmailViewerProps): JSX.Element {
  const { file } = props.windowProps;
  const { data: buffer, loading: fetching, error: fetchError } = useFileContents(file, "arrayBuffer");
  const [email, setEmail] = useState<Email | null>(null);
  const [parseError, setParseError] = useState<boolean>(false);

  // postal-mime parsing is async, so it runs in an effect rather than a memo. The mounted flag
  // drops a stale parse if the buffer changes (a different email) before this one resolves.
  useEffect(() => {
    let active = true;
    const parse = async (): Promise<void> => {
      if (!buffer) {
        if (active) {
          setEmail(null);
          setParseError(false);
        }
        return;
      }
      try {
        const parsed = await new PostalMime().parse(buffer);
        if (!active) return;
        setEmail(parsed);
        setParseError(false);
      } catch (err) {
        if (!active) return;
        console.error("Failed to parse email file:", err);
        setParseError(true);
      }
    };
    void parse();
    return () => {
      active = false;
    };
  }, [buffer]);

  const loading = fetching || (!!buffer && !email && !parseError);
  const error = fetchError || parseError;
  const attachments: Attachment[] = email?.attachments ?? [];
  const hasTextBody = !!email?.text?.trim();

  return (
    <ViewerShell file={file} loading={loading} error={error}>
      {email && (
        <EmailBox>
          <Box className="emailHeaders">
            <HeaderRow label="From" value={formatAddresses(email.from ? [email.from] : undefined)} />
            <HeaderRow label="To" value={formatAddresses(email.to)} />
            <HeaderRow label="Cc" value={formatAddresses(email.cc)} />
            <HeaderRow label="Subject" value={email.subject} />
            <HeaderRow label="Date" value={formatDate(email.date)} />
          </Box>
          {attachments.length > 0 && (
            <>
              <Divider />
              <Box className="emailAttachments">
                <Typography component="span" variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>
                  Attachments ({attachments.length})
                </Typography>
                {attachments.map((attachment, index) => (
                  <Chip
                    key={`${attachment.filename ?? "unnamed"}-${index}`}
                    size="small"
                    label={attachment.filename ?? "(unnamed)"}
                  />
                ))}
              </Box>
            </>
          )}
          <Divider />
          <Box className="emailBody">
            {email.html ? (
              <Box
                component="iframe"
                title="Email body"
                // Allow user-initiated links (via <base target="_blank">) to open in a normal tab; scripts are still blocked by sandbox + CSP.
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={buildEmailSrcDoc(email.html)}
              />
            ) : hasTextBody ? (
              <pre className="emailBodyText">{email.text}</pre>
            ) : (
              <Alert severity="info" variant="filled" sx={{ m: 1 }}>
                This email has no readable body.
              </Alert>
            )}
          </Box>
        </EmailBox>
      )}
    </ViewerShell>
  );
}
